// src/components/layout/Sidebar.tsx

import React, { useMemo, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileText, Edit, BookOpen, LogOut, ChevronLeft, ChevronRight, Award,
  LayoutDashboard, X, BadgeCheck, GitBranch, MessageCircle,
  FolderOpen, ChevronDown, ClipboardCheck, BarChart2, ScrollText,
  Award as AwardIcon, AlertTriangle, ShieldAlert, TrendingUp, Users,
  GraduationCap, Settings,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useSidebar } from '@/context/SidebarContext';
import { useIsMobile } from '@/hooks/useIsMobile';
import type { ViewId } from '@/services/userViews.service';
import type { UserRole } from '@/config/routes.config';
import { materialDidacticoService } from '@/services/materialDidactico.service';

// ─── Types ───────────────────────────────────────────────────────────────────

interface MenuItem {
  path: string;
  label: string;
  icon: React.ElementType;
  roles: UserRole[];
  viewId: ViewId;
}

type AccentColor = 'blue' | 'violet' | 'emerald' | 'amber' | 'rose' | 'cyan' | 'purple' | 'teal' | 'pink' | 'slate';

// ─── Group ID buckets ─────────────────────────────────────────────────────────

const DOCS_GROUP_IDS:         ViewId[] = ['documents','approvals','my_documents','master_list'];
const CONFIG_GROUP_IDS:       ViewId[] = ['app_settings'];
const AUDITORIA_GROUP_IDS:    ViewId[] = ['auditoria_interna','auditoria_proveedor','auditoria_certificacion'];
const GERENCIAL_GROUP_IDS:    ViewId[] = ['monitoreo_indicadores','acciones_gerencial','minuta_gerencial'];
const CERT_GROUP_IDS:         ViewId[] = ['certificado_iatf'];
const RIESGOS_GROUP_IDS:      ViewId[] = ['riesgos_matriz','riesgos_tratamiento','riesgos_monitoreo'];
const CONTINGENCIA_GROUP_IDS: ViewId[] = ['contingencia_programa','contingencia_simulaciones','contingencia_resultados'];
const CLIENTES_GROUP_IDS:     ViewId[] = ['clientes_encuestas','clientes_concentrado','clientes_difusion'];
const MEJORAS_GROUP_IDS:      ViewId[] = ['mejoras_procesos','mejoras_documentacion','mejoras_personal','mejoras_indicadores','mejoras_cliente'];
const MATERIAL_GROUP_IDS:     ViewId[] = ['material_didactico'];
const ADMIN_GROUP_IDS:        ViewId[] = [];
const FOOTER_VIEW_IDS:        ViewId[] = ['app_change_control','whatsapp'];

const APP_CHANGE_CONTROL_PATH = '/admin/app-change-control';

const ALL_GROUP_IDS: ViewId[] = [
  ...DOCS_GROUP_IDS, ...AUDITORIA_GROUP_IDS, ...GERENCIAL_GROUP_IDS, ...CERT_GROUP_IDS,
  ...RIESGOS_GROUP_IDS, ...CONTINGENCIA_GROUP_IDS, ...CLIENTES_GROUP_IDS, ...MEJORAS_GROUP_IDS, ...MATERIAL_GROUP_IDS,
  ...ADMIN_GROUP_IDS, ...CONFIG_GROUP_IDS,
];

// ─── Groups config ────────────────────────────────────────────────────────────

const GROUPS = [
  { id: 'documentos',   label: 'Documentos',                  icon: FolderOpen,     ids: DOCS_GROUP_IDS,         accent: 'blue'    as AccentColor },
  { id: 'auditoria',    label: 'Auditorías',                   icon: ClipboardCheck, ids: AUDITORIA_GROUP_IDS,    accent: 'violet'  as AccentColor },
  { id: 'gerencial',    label: 'Revisión Gerencial',           icon: BarChart2,      ids: GERENCIAL_GROUP_IDS,    accent: 'emerald' as AccentColor },
  { id: 'certificado',  label: 'Certificado',                  icon: AwardIcon,      ids: CERT_GROUP_IDS,         accent: 'amber'   as AccentColor },
  { id: 'riesgos',      label: 'Análisis de Riesgos',          icon: AlertTriangle,  ids: RIESGOS_GROUP_IDS,      accent: 'rose'    as AccentColor },
  { id: 'contingencia', label: 'Plan de Contingencia',         icon: ShieldAlert,    ids: CONTINGENCIA_GROUP_IDS, accent: 'cyan'    as AccentColor },
  { id: 'clientes',     label: 'Req. Específicos Clientes',    icon: Users,          ids: CLIENTES_GROUP_IDS,     accent: 'purple'  as AccentColor },
  { id: 'mejoras',      label: 'Mejoras',                      icon: TrendingUp,     ids: MEJORAS_GROUP_IDS,      accent: 'teal'    as AccentColor },
  { id: 'material',     label: 'Material Didáctico',           icon: GraduationCap,  ids: MATERIAL_GROUP_IDS,     accent: 'pink'    as AccentColor },
  { id: 'configuracion', label: 'Configuraciones',   icon: Settings,  ids: CONFIG_GROUP_IDS, accent: 'slate' as AccentColor },
];

// ─── Accent palette ───────────────────────────────────────────────────────────

const ACCENT_HEX: Record<AccentColor, string> = {
  blue:    '#3b82f6',
  violet:  '#8b5cf6',
  emerald: '#10b981',
  amber:   '#f59e0b',
  rose:    '#f43f5e',
  cyan:    '#06b6d4',
  purple:  '#a855f7',
  teal:    '#14b8a6',
  pink:    '#ec4899',
  slate:   '#64748b',
};

// ─── Menu items ───────────────────────────────────────────────────────────────

const menuItems: MenuItem[] = [
  { path: '/quality/dashboard',               label: 'Dashboard',                        icon: LayoutDashboard, roles: ['Administrador_Calidad','Responsable_Calidad'], viewId: 'dashboard' },
  { path: '/quality/documents',               label: 'Documentos',                       icon: FileText,        roles: ['Administrador_Calidad','Responsable_Calidad'], viewId: 'documents' },
  { path: '/quality/approvals',               label: 'Solicitudes',                      icon: BadgeCheck,      roles: ['Administrador_Calidad','Responsable_Calidad'], viewId: 'approvals' },
  { path: '/creator/dashboard',               label: 'Mis Documentos',                   icon: Edit,            roles: ['Administrador_Calidad','Creador_Documentos'],  viewId: 'my_documents' },
  { path: '/documents/view',                  label: 'Lista maestra de Documentos',      icon: BookOpen,        roles: ['Administrador_Calidad','Lector','Creador_Documentos','Auditor_Interno','Aprobador','Revisor'], viewId: 'master_list' },
  { path: '/auditoria/interna',               label: '1ra Parte (Interna)',              icon: ClipboardCheck,  roles: ['Administrador_Calidad'], viewId: 'auditoria_interna' },
  { path: '/auditoria/proveedor-cliente',     label: '2da Parte (Proveedor/Cliente)',    icon: ClipboardCheck,  roles: ['Administrador_Calidad'], viewId: 'auditoria_proveedor' },
  { path: '/auditoria/certificacion',         label: '3ra Parte (Certificación)',        icon: ClipboardCheck,  roles: ['Administrador_Calidad'], viewId: 'auditoria_certificacion' },
  { path: '/revision-gerencial/monitoreo',    label: 'Monitoreo de Indicadores',         icon: BarChart2,       roles: ['Administrador_Calidad'], viewId: 'monitoreo_indicadores' },
  { path: '/revision-gerencial/acciones',     label: 'Acciones',                         icon: ScrollText,      roles: ['Administrador_Calidad'], viewId: 'acciones_gerencial' },
  { path: '/revision-gerencial/minuta',       label: 'Minuta Gerencial',                 icon: ScrollText,      roles: ['Administrador_Calidad'], viewId: 'minuta_gerencial' },
  { path: '/certificado/iatf',                label: 'Certificado IATF',                 icon: AwardIcon,       roles: ['Administrador_Calidad'], viewId: 'certificado_iatf' },
  { path: '/analisis-riesgos/matriz',         label: 'Matriz de Análisis de Riesgos',   icon: AlertTriangle,   roles: ['Administrador_Calidad'], viewId: 'riesgos_matriz' },
  { path: '/analisis-riesgos/tratamiento',    label: 'Tratamiento del Riesgo',           icon: ShieldAlert,     roles: ['Administrador_Calidad'], viewId: 'riesgos_tratamiento' },
  { path: '/analisis-riesgos/monitoreo',      label: 'Monitoreo y Revisión',             icon: BarChart2,       roles: ['Administrador_Calidad'], viewId: 'riesgos_monitoreo' },
  { path: '/plan-contingencia/programa',      label: 'Programa de Plan de Contingencia', icon: ScrollText,      roles: ['Administrador_Calidad'], viewId: 'contingencia_programa' },
  { path: '/plan-contingencia/simulaciones',  label: 'Simulaciones',                     icon: ClipboardCheck,  roles: ['Administrador_Calidad'], viewId: 'contingencia_simulaciones' },
  { path: '/plan-contingencia/resultados',    label: 'Resultados',                       icon: BarChart2,       roles: ['Administrador_Calidad'], viewId: 'contingencia_resultados' },
  { path: '/requisitos-clientes/encuestas',   label: 'Encuestas de Satisfacción',        icon: Users,           roles: ['Administrador_Calidad'], viewId: 'clientes_encuestas' },
  { path: '/requisitos-clientes/concentrado', label: 'Concentrado de Requerimientos',    icon: FileText,        roles: ['Administrador_Calidad'], viewId: 'clientes_concentrado' },
  { path: '/requisitos-clientes/difusion',    label: 'Difusión',                         icon: MessageCircle,   roles: ['Administrador_Calidad'], viewId: 'clientes_difusion' },
  { path: '/mejoras/procesos',                label: 'Procesos',                         icon: TrendingUp,      roles: ['Administrador_Calidad'], viewId: 'mejoras_procesos' },
  { path: '/mejoras/documentacion',           label: 'Documentación',                    icon: FileText,        roles: ['Administrador_Calidad'], viewId: 'mejoras_documentacion' },
  { path: '/mejoras/personal',                label: 'Personal',                         icon: Users,           roles: ['Administrador_Calidad'], viewId: 'mejoras_personal' },
  { path: '/mejoras/indicadores',             label: 'Indicadores',                      icon: BarChart2,       roles: ['Administrador_Calidad'], viewId: 'mejoras_indicadores' },
  { path: '/mejoras/cliente',                 label: 'Cliente',                          icon: Users,           roles: ['Administrador_Calidad'], viewId: 'mejoras_cliente' },
  { path: '/material-didactico',              label: 'Material Didáctico',               icon: GraduationCap,   roles: ['Administrador_Calidad'], viewId: 'material_didactico' },
  { path: APP_CHANGE_CONTROL_PATH,            label: 'Control de cambios de la app',     icon: GitBranch,       roles: ['Administrador_Calidad'], viewId: 'app_change_control' },
  { path: '/admin/whatsapp',                  label: 'Notificaciones WhatsApp',          icon: MessageCircle,   roles: ['Administrador_Calidad'], viewId: 'whatsapp' },
  { path: '/admin/settings',                  label: 'Configuración',                    icon: Settings,        roles: ['Administrador_Calidad'], viewId: 'app_settings' },
];

// ─── NavItem ──────────────────────────────────────────────────────────────────

interface NavItemProps {
  item: MenuItem;
  accent: AccentColor;
  isActive: boolean;
  onNavClick: () => void;
  index: number;
  alert?: boolean;
}

const NavItem: React.FC<NavItemProps> = ({ item, accent, isActive, onNavClick, index, alert }) => {
  const [hovered, setHovered] = useState(false);
  const color = ACCENT_HEX[accent];
  const Icon = item.icon;

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.04, duration: 0.2, ease: 'easeOut' }}
    >
      <Link to={item.path} onClick={onNavClick}>
        <div
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          className="relative flex items-center gap-2.5 px-3 py-[7px] rounded-lg cursor-pointer transition-colors duration-150"
          style={{
            backgroundColor: isActive
              ? `${color}1a`
              : hovered
              ? 'rgba(255,255,255,0.04)'
              : 'transparent',
            borderLeft: isActive
              ? `2px solid ${color}cc`
              : '2px solid transparent',
          }}
        >
          <Icon
            className="h-[14px] w-[14px] flex-shrink-0 transition-colors duration-150"
            style={{ color: isActive ? color : hovered ? '#94a3b8' : '#94a3b8' }}
          />
          <span
            className="text-[12px] font-medium truncate leading-tight transition-colors duration-150"
            style={{ color: isActive ? '#e2e8f0' : hovered ? '#cbd5e1' : '#cbd5e1' }}
          >
            {item.label}
          </span>
          {alert && (
            <span className="relative inline-flex h-2 w-2 mx-1">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
            </span>
          )}
          {isActive && (
            <div
              className="absolute right-2.5 w-1.5 h-1.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: color, boxShadow: `0 0 6px ${color}` }}
            />
          )}
        </div>
      </Link>
    </motion.div>
  );
};

// ─── NavGroup ─────────────────────────────────────────────────────────────────

interface NavGroupProps {
  id: string;
  label: string;
  icon: React.ElementType;
  items: MenuItem[];
  open: boolean;
  onToggle: () => void;
  collapsed: boolean;
  showLabels: boolean;
  isActive: (path: string) => boolean;
  onNavClick: () => void;
  accent: AccentColor;
  alertPaths?: string[];
}

const NavGroup: React.FC<NavGroupProps> = ({
  id, label, icon: GroupIcon, items, open, onToggle,
  collapsed, showLabels, isActive, onNavClick, accent, alertPaths,
}) => {
  const color = ACCENT_HEX[accent];
  const hasActive = items.some((i) => isActive(i.path));
  const hasAlert = items.some((item) => alertPaths?.includes(item.path) ?? false);

  return (
    <div
      className="rounded-xl overflow-hidden transition-all duration-300"
      style={
        open
          ? {
              background: `linear-gradient(135deg, ${color}0d 0%, transparent 100%)`,
              boxShadow: `inset 0 0 0 1px ${color}28, 0 2px 8px ${color}0a`,
            }
          : hasActive
          ? { boxShadow: `inset 0 0 0 1px ${color}18` }
          : {}
      }
    >
      {/* ── Header ── */}
      <button
        type="button"
        onClick={() => !collapsed && onToggle()}
        title={collapsed ? label : undefined}
        className={`w-full flex items-center gap-2.5 px-3 py-2.5 transition-all duration-200 ${collapsed ? 'justify-center' : ''}`}
        style={
          open
            ? { borderLeft: `2px solid ${color}aa` }
            : hasActive
            ? { borderLeft: `2px solid ${color}44` }
            : { borderLeft: '2px solid transparent' }
        }
      >
        {/* Icon pill */}
        <div
          className="flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-lg transition-all duration-200"
          style={{
            backgroundColor: open || hasActive ? `${color}22` : 'rgba(255,255,255,0.05)',
            boxShadow: open ? `0 0 12px ${color}30` : 'none',
          }}
        >
          <GroupIcon
            className="h-[15px] w-[15px] transition-all duration-200"
            style={{ color: open || hasActive ? color : '#94a3b8' }}
          />
        </div>

        {showLabels && (
          <>
            <span
              className="text-[11px] font-bold flex-1 text-left truncate uppercase tracking-wider transition-colors duration-200"
              style={{ color: open ? '#e2e8f0' : hasActive ? '#94a3b8' : '#94a3b8' }}
            >
              {label}
            </span>

            <div className="flex items-center gap-1.5 flex-shrink-0">
              {/* Alert dot */}
              {hasAlert && (
                <span className="relative inline-flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
                </span>
              )}

              {/* Item count badge */}
              <span
                className="text-[10px] font-bold px-1.5 py-0.5 rounded-md tabular-nums leading-none transition-all duration-200"
                style={{
                  backgroundColor: open ? `${color}25` : 'rgba(255,255,255,0.05)',
                  color: open ? color : '#64748b',
                }}
              >
                {items.length}
              </span>

              {/* Chevron */}
              <ChevronDown
                className="h-[13px] w-[13px] transition-transform duration-300"
                style={{
                  color: open ? color : '#64748b',
                  transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              />
            </div>
          </>
        )}
      </button>

      {/* ── Expanded items ── */}
      <AnimatePresence initial={false}>
        {showLabels && open && (
          <motion.div
            key={`grp-${id}`}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.24, ease: [0.4, 0, 0.2, 1] }}
            className="overflow-hidden"
          >
            {/* Subtle separator line */}
            <div className="mx-3 mb-1.5" style={{ height: '1px', background: `linear-gradient(90deg, ${color}40, transparent)` }} />

            <div className="px-2 pb-2 space-y-0.5">
              {items.map((item, i) => (
                <NavItem
                  key={item.path}
                  item={item}
                  accent={accent}
                  isActive={isActive(item.path)}
                  onNavClick={onNavClick}
                  index={i}
                  alert={alertPaths?.includes(item.path) ?? false}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Collapsed: icon-only ── */}
      {collapsed &&
        items.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          const itemAlert = alertPaths?.includes(item.path) ?? false;
          return (
            <Link key={`c-${item.path}`} to={item.path} onClick={onNavClick}>
              <div
                title={item.label}
                className="relative flex items-center justify-center mx-1 my-0.5 p-2.5 rounded-lg cursor-pointer transition-all duration-150"
                style={active ? { backgroundColor: `${color}20` } : {}}
              >
                <Icon
                  className="h-4 w-4 transition-colors"
                  style={{ color: active ? color : '#94a3b8' }}
                />
                {itemAlert && (
                  <span className="absolute top-0.5 right-0.5 h-2 w-2 rounded-full bg-red-500 border border-slate-900 animate-pulse" />
                )}
              </div>
            </Link>
          );
        })}
    </div>
  );
};

// ─── Role labels ──────────────────────────────────────────────────────────────

const roleLabels: Record<UserRole, string> = {
  Administrador_Calidad: 'Administrador',
  Responsable_Calidad:   'Responsable de Calidad',
  Auditor_Interno:       'Auditor Interno',
  Creador_Documentos:    'Jefe',
  Aprobador:             'Aprobador',
  Revisor:               'Revisor',
  Lector:                'Visitante',
};

// ─── Sidebar ──────────────────────────────────────────────────────────────────

export const Sidebar: React.FC = () => {
  const { collapsed, toggleSidebar, mobileOpen, closeMobile } = useSidebar();
  const isMobile    = useIsMobile();
  const location    = useLocation();
  const navigate    = useNavigate();
  const { user, logout, hasViewAccess } = useAuth();

  // ── Accordion state ─────────────────────────────────────────────────────────
  const getGroupForPath = (pathname: string): string | null => {
    for (const g of GROUPS) {
      if (menuItems.some((item) => (g.ids as string[]).includes(item.viewId) && item.path === pathname)) {
        return g.id;
      }
    }
    return null;
  };

  const [openGroup, setOpenGroup] = useState<string | null>(() => getGroupForPath(location.pathname));
  const toggleGroup = (id: string) => setOpenGroup((g) => (g === id ? null : id));

  // Auto-open group when navigating to a page inside it
  useEffect(() => {
    const g = getGroupForPath(location.pathname);
    if (g) setOpenGroup(g);
  }, [location.pathname]);

  // ── Material didáctico alerts ───────────────────────────────────────────────
  const [missingMaterialDept, setMissingMaterialDept] = useState(false);
  useEffect(() => {
    const unsub = materialDidacticoService.subscribe((slides) => {
      setMissingMaterialDept(slides.some((s) => !s.department));
    });
    return unsub;
  }, []);

  const alertPaths = useMemo(() => {
    const paths: string[] = [];
    if (missingMaterialDept) paths.push('/material-didactico');
    return paths;
  }, [missingMaterialDept]);

  // ── Filtered items ──────────────────────────────────────────────────────────
  const visibleItems = useMemo(() => {
    if (!user) return [];
    return menuItems.filter((item) => hasViewAccess(item.viewId));
  }, [user, hasViewAccess]);

  const uniqueItems = visibleItems.filter(
    (item, idx, arr) => arr.findIndex((i) => i.path === item.path) === idx
  );

  const isActive    = (path: string) => location.pathname === path;

  const topNavItems          = uniqueItems.filter((item) => !ALL_GROUP_IDS.includes(item.viewId) && !FOOTER_VIEW_IDS.includes(item.viewId));
  const footerNavItems       = uniqueItems.filter((item) => FOOTER_VIEW_IDS.includes(item.viewId) && item.path !== APP_CHANGE_CONTROL_PATH);
  const showAppChangeControl = !!user && hasViewAccess('app_change_control');

  const groupItems = useMemo(() =>
    GROUPS.map((g) => ({
      ...g,
      items: uniqueItems.filter((item) => (g.ids as string[]).includes(item.viewId)),
    })).filter((g) => g.items.length > 0),
  [uniqueItems]);
  const showLabels  = isMobile ? true : !collapsed;
  const sidebarX    = isMobile ? (mobileOpen ? 0 : -256) : 0;
  const sidebarWidth = isMobile ? 256 : collapsed ? 68 : 256;

  const initials = user?.fullName
    ? user.fullName.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase()
    : '?';

  const handleLogout = async () => { await logout(); navigate('/login', { replace: true }); };
  const handleNavClick = () => { if (isMobile) closeMobile(); };
  const handleGoHome   = () => { if (isMobile) closeMobile(); navigate('/', { replace: false }); };

  return (
    <>
      {/* Mobile overlay */}
      <AnimatePresence>
        {isMobile && mobileOpen && (
          <motion.div
            key="overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40"
            onClick={closeMobile}
          />
        )}
      </AnimatePresence>

      {/* ── Sidebar shell ──────────────────────────────────────────────────── */}
      <motion.div
        animate={{ x: sidebarX, width: sidebarWidth }}
        transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
        className="fixed left-0 top-0 h-screen z-50 flex flex-col overflow-hidden bg-slate-900/95 backdrop-blur-xl"
        style={{
          borderRight: '1px solid rgba(255,255,255,0.10)',
          boxShadow: '4px 0 24px rgba(0,0,0,0.25)',
        }}
      >
        {/* ── Header ── */}
        <div
          className="flex items-center justify-between min-h-[60px] px-4"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
        >
          {showLabels && (
            <motion.button
              type="button"
              onClick={handleGoHome}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex items-center gap-3 rounded-lg px-1 py-1 hover:bg-white/5 transition-colors text-left overflow-hidden"
              title="Ir al inicio"
            >
              <div
                className="p-2 rounded-lg flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)', boxShadow: '0 0 12px #3b82f640' }}
              >
                <Award className="h-4 w-4 text-white" />
              </div>
              <div className="min-w-0">
                <h2 className="text-sm font-black text-white leading-tight tracking-tight">IATF 16949</h2>
                <p className="text-[10px] text-slate-500 tracking-wide uppercase">Sistema de Calidad</p>
              </div>
            </motion.button>
          )}
          <button
            onClick={isMobile ? closeMobile : toggleSidebar}
            className="p-2 rounded-lg hover:bg-white/8 transition-colors flex-shrink-0 ml-auto"
            style={{ color: '#64748b' }}
          >
            {isMobile ? <X className="h-4 w-4" /> : collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        </div>

        {/* ── User card ── */}
        {user && (
          <div
            className={`transition-all duration-300 ${showLabels ? 'px-4 py-3' : 'px-3 py-3 flex justify-center'}`}
            style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}
          >
            {showLabels ? (
              <div className="flex items-center gap-3">
                {user.photoUrl ? (
                  <img src={user.photoUrl} alt={user.fullName || ''} className="w-9 h-9 rounded-xl object-cover flex-shrink-0" style={{ border: '1px solid rgba(255,255,255,0.15)' }} />
                ) : (
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 text-sm font-black text-white"
                    style={{ background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)', boxShadow: '0 0 10px #3b82f630' }}
                  >
                    {initials}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-semibold text-slate-200 truncate leading-tight">{user.fullName}</p>
                  <p className="text-[11px] truncate mt-0.5" style={{ color: '#3b82f6' }}>{roleLabels[user.roleSGC]}</p>
                </div>
              </div>
            ) : (
              user.photoUrl
                ? <img src={user.photoUrl} alt="" className="w-8 h-8 rounded-xl object-cover" style={{ border: '1px solid rgba(255,255,255,0.15)' }} />
                : <div className="w-8 h-8 rounded-xl flex items-center justify-center text-xs font-black text-white" style={{ background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)' }}>{initials}</div>
            )}
          </div>
        )}

        {/* ── Navigation ── */}
        <nav className="flex-1 overflow-y-auto px-2 py-2 space-y-0.5"
          style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.08) transparent' }}
        >
          {/* Top-level items (Dashboard, etc.) */}
          {topNavItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Link key={item.path} to={item.path} onClick={handleNavClick}>
                <motion.div
                  whileHover={{ x: collapsed ? 0 : 2 }}
                  whileTap={{ scale: 0.98 }}
                  title={!showLabels ? item.label : undefined}
                  className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl cursor-pointer transition-all duration-150"
                  style={{
                    backgroundColor: active ? 'rgba(59,130,246,0.15)' : undefined,
                    boxShadow: active ? 'inset 0 0 0 1px rgba(59,130,246,0.25)' : undefined,
                  }}
                >
                  <Icon className="h-[15px] w-[15px] flex-shrink-0 transition-colors" style={{ color: active ? '#3b82f6' : '#94a3b8' }} />
                  {showLabels && (
                    <span className="text-[13px] font-semibold truncate transition-colors" style={{ color: active ? '#e2e8f0' : '#94a3b8' }}>
                      {item.label}
                    </span>
                  )}
                </motion.div>
              </Link>
            );
          })}

          {/* Separator if there are top items + groups */}
          {topNavItems.length > 0 && groupItems.length > 0 && (
            <div className="mx-3 my-2" style={{ height: '1px', background: 'rgba(255,255,255,0.05)' }} />
          )}

          {/* Accordion groups */}
          <div className="space-y-0.5">
            {groupItems.map((g) => (
              <NavGroup
                key={g.id}
                id={g.id}
                label={g.label}
                icon={g.icon}
                items={g.items}
                open={openGroup === g.id}
                onToggle={() => toggleGroup(g.id)}
                collapsed={collapsed}
                showLabels={showLabels}
                isActive={isActive}
                onNavClick={handleNavClick}
                accent={g.accent}
                alertPaths={alertPaths}
              />
            ))}
          </div>
        </nav>

        {/* ── Footer ── */}
        <div className="px-2 py-2 space-y-0.5" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          {footerNavItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Link key={item.path} to={item.path} onClick={handleNavClick}>
                <div
                  title={!showLabels ? item.label : undefined}
                  className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl cursor-pointer transition-all duration-150 hover:bg-white/5"
                  style={collapsed ? { justifyContent: 'center' } : {}}
                >
                  <Icon className="h-4 w-4 flex-shrink-0" style={{ color: active ? '#3b82f6' : '#94a3b8' }} />
                  {showLabels && <span className="text-[12px] font-medium" style={{ color: active ? '#e2e8f0' : '#94a3b8' }}>{item.label}</span>}
                </div>
              </Link>
            );
          })}

          {showAppChangeControl && (
            <Link to={APP_CHANGE_CONTROL_PATH} onClick={handleNavClick}>
              <div
                title={!showLabels ? 'Control de cambios' : undefined}
                className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl cursor-pointer transition-all duration-150 hover:bg-white/5"
                style={collapsed ? { justifyContent: 'center' } : {}}
              >
                <GitBranch className="h-4 w-4 flex-shrink-0" style={{ color: isActive(APP_CHANGE_CONTROL_PATH) ? '#06b6d4' : '#94a3b8' }} />
                {showLabels && <span className="text-[12px] font-medium" style={{ color: isActive(APP_CHANGE_CONTROL_PATH) ? '#e2e8f0' : '#94a3b8' }}>Control de cambios</span>}
              </div>
            </Link>
          )}

          <button
            onClick={handleLogout}
            title={!showLabels ? 'Cerrar Sesión' : undefined}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl transition-all duration-150 group/logout"
            style={collapsed ? { justifyContent: 'center' } : {}}
          >
            <LogOut className="h-4 w-4 flex-shrink-0 transition-colors group-hover/logout:text-red-400" style={{ color: '#64748b' }} />
            {showLabels && <span className="text-[12px] font-medium transition-colors group-hover/logout:text-red-300" style={{ color: '#64748b' }}>Cerrar Sesión</span>}
          </button>
        </div>
      </motion.div>
    </>
  );
};
